import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "./NtText";
import NtTouchableEffect from "./NtTouchableEffect";
import NtMaterialIcon from "./NtMaterialIcon";
import { useTheme } from "../context/ThemeContext";

/**
 * Prev/Next pagination strip. Used at the bottom of every list card.
 *
 *   <NtPagination
 *     page={page}
 *     pageSize={25}
 *     total={280}
 *     onChange={(nextPage) => ...}
 *   />
 */
function NtPagination({ page = 1, pageSize = 25, total = 0, onChange, style }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  const canPrev = page > 1;
  const canNext = page < pageCount;

  const goPrev = () => canPrev && onChange && onChange(page - 1);
  const goNext = () => canNext && onChange && onChange(page + 1);

  return (
    <View style={[styles.wrap, style]}>
      <NtText style={styles.info}>
        {total === 0
          ? "No records"
          : `Showing ${start}–${end} of ${total}`}
      </NtText>

      <View style={{ flex: 1 }} />

      <NtTouchableEffect
        style={[styles.btn, !canPrev && styles.btnDisabled]}
        onPress={goPrev}
      >
        <NtMaterialIcon name="chevron-left" size={14} color={canPrev ? theme.text : theme.textMute} />
        <NtText style={[styles.btnLabel, !canPrev && styles.btnLabelDisabled]}>Prev</NtText>
      </NtTouchableEffect>

      <NtText style={styles.pageIndicator}>
        Page {page} of {pageCount}
      </NtText>

      <NtTouchableEffect
        style={[styles.btn, !canNext && styles.btnDisabled]}
        onPress={goNext}
      >
        <NtText style={[styles.btnLabel, !canNext && styles.btnLabelDisabled]}>Next</NtText>
        <NtMaterialIcon name="chevron-right" size={14} color={canNext ? theme.text : theme.textMute} />
      </NtTouchableEffect>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    wrap: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      paddingHorizontal: 14,
      paddingVertical: 10,
      borderTopWidth: 1,
      borderTopColor: t.borderSoft,
    },
    info: { fontSize: 11, color: t.textDim },
    btn: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
      paddingHorizontal: 9,
      paddingVertical: 5,
      borderRadius: t.radius,
      borderWidth: 1,
      borderColor: t.border,
      backgroundColor: t.surface2,
    },
    btnDisabled: { opacity: 0.4 },
    btnLabel: { fontSize: 11, color: t.text, fontWeight: "500" },
    btnLabelDisabled: { color: t.textMute },
    pageIndicator: {
      fontFamily: "Menlo",
      fontSize: 11,
      color: t.textDim,
      minWidth: 80,
      textAlign: "center",
    },
  });

export default NtPagination;
